import { MapData } from '@/features/maps/core/types/MapData';
import { CalculationInput, CalculationResult } from '@/types';

interface CreateMapParams {
    userId: string;
    input: CalculationInput;
    result: CalculationResult;
    notes?: string | null;
    labels?: string[];
    id?: string; // Optional ID for existing maps or explicit ID generation
    createdAt?: string;
}

/**
 * Creates a flat MapData object from structured calculation results.
 * Centralizes the mapping logic to ensure consistency across the app.
 */
export function createMapFromCalculation({
    userId,
    input,
    result,
    notes = null,
    labels = [],
    id,
    createdAt
}: CreateMapParams): MapData {
    const now = new Date().toISOString();

    return {
        id: id || `temp-${Date.now()}`, // Default to temp ID if not provided
        user_id: userId,

        // Personal Details
        firstName: input.firstName,
        familyName: input.familyName,
        motherName: input.motherName,
        birthDate: input.birthDate,

        // Flattened Calculation Fields - Zodiac
        inner_sign_number: result.innerSign.number,
        inner_sign_name: result.innerSign.sign,
        age: result.innerSign.age || 0,

        // Flattened Calculation Fields - Gematria
        surrounding_sign_number: result.surroundingSign.number,
        surrounding_sign_name: result.surroundingSign.sign,
        gematria_value: result.surroundingSign.gematria,

        // Flattened Calculation Fields - Profile
        profile_number: result.profile.profileNumber,
        profile_sign: result.profile.profileSign,
        zodiac_position: result.profile.zodiacPosition,

        // Flattened Calculation Fields - Tur
        tur_value: result.tur.value,
        tur_label: result.tur.label,

        // Flattened Numerology Fields
        day_number: result.numerology.dayNumber,
        month_number: result.numerology.monthNumber,
        year_number: result.numerology.yearNumber,
        total_number: result.numerology.totalNumber,
        total_from_all_digits: result.numerology.totalFromAllDigits,
        first_number: result.numerology.firstNumber,
        family_number: result.numerology.familyNumber,
        total_name_number: result.numerology.totalNameNumber,
        ehevi_number: result.numerology.eheviNumber,
        itzurim_number: result.numerology.itzurimNumber,
        ehevi_inner_sum_first: result.numerology.eheviInnerSumFirst,
        ehevi_inner_sum_family: result.numerology.eheviInnerSumFamily,
        itzurim_inner_sum_first: result.numerology.itzurimInnerSumFirst,
        itzurim_inner_sum_family: result.numerology.itzurimInnerSumFamily,
        full_inner_sum: result.numerology.fullInnerSum,
        ehevi_inner_sum: result.numerology.eheviInnerSum,
        itzurim_inner_sum: result.numerology.itzurimInnerSum,
        full2_inner_sum: result.numerology.full2InnerSum,
        outer_sum: result.numerology.hamashaOuterSum,
        hamasha_inner_sum: result.numerology.hamashaInnerSum,

        // Metadata
        notes: notes,
        labels: labels,
        created_at: createdAt || now,
        updated_at: now,
        calculation_version: '1.0',

        // Birth Details (Phase 2)
        birth_time: input.birthTime || null,
        birth_location: input.cityName || null,
        birth_lat: input.lat || null,
        birth_lon: input.lng || null,
        timezone: input.timezone || null,
        astrology_cache: null,
        completeness_flags: result.completenessFlags || null
    };
}
